import Avatar from '@material-ui/core/Avatar';
import Checkbox from '@material-ui/core/Checkbox';
import Fab from '@material-ui/core/Fab';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Add from '@material-ui/icons/Add';
import Close from '@material-ui/icons/Close';
import CloudDownload from '@material-ui/icons/CloudDownload';
import Delete from '@material-ui/icons/Delete';
import LocalOffer from '@material-ui/icons/LocalOffer';
import Send from '@material-ui/icons/Send';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Collections } from 'Collections';
import { useAppUser } from 'components/AppUserProvider';
import { FullscreenLoading } from 'components/FullscreenLoading';
import { useOrg } from 'components/OrgProvider';
import { useOrgUser } from 'components/OrgUserProvider';
import { PageNavigation } from 'components/PageNavigation';
import { Snacbars } from 'components/Snacbars';
import { analytics } from 'FirebaseConfigs';
import { FirebaseUtils } from 'firebaseUtils/FirebaseUtils';
import { useUrlParam } from 'hooks/useUrlParam';
import { AnalyticsEvent } from 'models/AnalyticsEvent';
import { DbDoc, removeRef, WithRef } from 'models/FbDoc';
import { Grant } from 'models/Grant';
import { Offer } from 'models/Offer';
import { getOrgUserGrants } from 'models/OrgUser';
import { Paths } from 'Paths';
import { dateFormat, dateTimeFormat } from 'Util/date.utils';

import { downloadOfferPDF, sendOfferEmail } from './OfferScreen';

type OfferItemProps = {
    offer: WithRef<DbDoc<Offer>>
    selected?: boolean
    setSelected?: (isSelected: boolean) => void
    drafts?: boolean
}

export function OfferItem({
    offer: {
        metadata,
        data: {
            id,
            price: {
                manualPrice,
                discount,
                finalPrice
            },
            contactInfo,
            address,
        }
    },
    selected,
    setSelected,
    drafts,
}: OfferItemProps) {
    //TODO: Support other currencies
    const { name } = contactInfo || {};
    const discountText = discount ? (
        `(Inkludert ${discount} ,- rabatt)`
    ) : '';

    function toggleSelected() {
        setSelected?.(!selected)
    }

    return (
        <ListItem
            key={id}
            button
            component={Link}
            to={(drafts ? Paths.OFFER_DRAFTS : Paths.OFFER) + '/' + id}
            divider
        >
            <ListItemIcon>
                <Avatar alt="Offer">
                    <LocalOffer />
                </Avatar>
            </ListItemIcon>
            <ListItemText
                id={id}
                color="primary"
                primary={(name?.fullName || 'Mangler navn') + ' - ' + (address?.addressString || 'Mangler adresse')}
                secondary={
                    <div>
                        <div>{`Opprettet: ${dateTimeFormat.format(metadata.createdAt.toDate())}`}</div>
                        <div>{`Pris: ${manualPrice || finalPrice},- ${manualPrice ? '' : discountText}`}</div>
                    </div>
                }
            />
            {setSelected ? (
                <ListItemSecondaryAction>
                    <Checkbox
                        edge="end"
                        onChange={toggleSelected}
                        checked={selected}
                        inputProps={{ 'aria-labelledby': id }}
                    />
                </ListItemSecondaryAction>
            ) : null}
        </ListItem>
    );
}

//function sortByField<T extends object>(field: keyof T, reverse: boolean = false) {
//    return (array: T[]): T[] => {
//        const sorted = array.sort(fieldComparator<T>(field));
//        if (reverse) {
//            return sorted.reverse();
//        }
//        return sorted;
//    }
//}

export function filterOffers(filter: string, offers?: WithRef<DbDoc<Offer>>[]) {
    if (!offers || !filter) {
        return offers;
    }
    const lowerCaseFilter = String(filter).toLowerCase();
    return offers.filter(({ data: offer, metadata }) => {
        const price = offer.price.finalPrice
        const searchableOffer = [
            offer.contactInfo?.name?.fullName,
            dateFormat.format(metadata.createdAt.toDate()),
            offer.id,
            price,
            !offer.price.discount ? '' : price - offer.price.discount,
            !offer.price.discount ? '' : offer.price.discount,
            offer.contactInfo?.email?.email,
            offer.contactInfo?.phone?.phoneNumberString,
            offer.address?.addressString,
            offer.offerCreator.orgUser?.orgContactInfo?.name?.fullName,
        ].join(', ').toLowerCase();
        return searchableOffer.includes(lowerCaseFilter);
    })
}

type Props = {
    drafts?: boolean
}

/*
function useFilterSame<T extends { id: string }>(primaryItems?: T[] | null, secondaryItems?: T[] | null) {
    return useMemo(() => {
        const primaryIds = primaryItems?.map(({ id }) => id).join(',')
        return [
            ...(primaryItems || []),
            ...(secondaryItems?.filter(i => !primaryIds?.includes(i.id)) || []),
        ]
    }, [primaryItems, secondaryItems])
}

const emptyArray: any[] = []
*/

export function Offers({ drafts }: Props) {
    const { enqueueSnackbar } = useSnackbar()
    const [org, loadingOrg] = useOrg()
    const [orgUserDoc, loadingOrgUser] = useOrgUser()
    const [appUserDoc, loadingAppUser] = useAppUser()
    const [reportError] = FirebaseUtils.useErrorLogs()

    const orgUser = orgUserDoc?.data || null
    const appUser = appUserDoc?.data || null

    const collection = drafts ? Collections.OFFER_DRAFTS : Collections.OFFERS
    const [offers, loadingOffers, error] = FirebaseUtils.useCollectionData<Offer>(
        !!drafts,
        collection,
        AnalyticsEvent.GET_LATEST_OFFERS_ERROR,
        {},
        (query, path) => (
            org && query.orderBy(path.metadata.createdAt, 'desc').where(path.data.org.id, '==', org.data.id)
        ))

    const [search, setSearch] = useState(useUrlParam('s'));
    const [selected, setSelected] = useState<string[]>([]);
    const grants = getOrgUserGrants(orgUser)
    const filtered = filterOffers(search, offers)

    const noOffersString = "Du har ingen " + (drafts ? 'utkast til anbud' : 'anbud') + (!!search ? ' som passer med søket' : '')

    const loading = loadingOffers || loadingOrgUser || loadingOrg || !org || loadingAppUser

    const multiOptions = !!selected.length ? (
        <ListItem divider>
            <ListItemText primary="Handling" />
            <ListItemSecondaryAction>
                {(drafts || grants[Grant.DELETE_OFFER]) ? (
                    <IconButton aria-label="delete-selected" onClick={() => {
                        FirebaseUtils.deleteDocumentBatch(
                            true,
                            collection,
                            selected,
                            appUser,
                            orgUser,
                            drafts ? AnalyticsEvent.DELETED_OFFER_DRAFTS : AnalyticsEvent.DELETED_OFFERS,
                            drafts ? AnalyticsEvent.DELETED_OFFER_DRAFTS : AnalyticsEvent.DELETED_OFFERS,
                            drafts ? AnalyticsEvent.DELETE_OFFER_DRAFTS_ERROR : AnalyticsEvent.DELETE_OFFERS_ERROR,
                            enqueueSnackbar,
                            'Slettet flere anbud',
                            (ids) => ({
                                [drafts ? 'offerDraftIds' : 'offerIds']: ids,
                            })
                        )
                        setSelected([])
                    }}>
                        <Delete />
                    </IconButton>
                ) : null}
                {!drafts && grants[Grant.SEND_OFFER] ? (
                    <IconButton aria-label="download-selected" onClick={() => {
                        selected.forEach(offerId => {
                            const offer = offers?.find(o => o.data.id === offerId)
                            if (offer) {
                                downloadOfferPDF(removeRef(offer))
                            } else {
                                reportError('Kunne ikke laste ned anbud', AnalyticsEvent.DOWNLOAD_OFFER_PDF_ERROR, {
                                    reason: 'Selected but not loaded',
                                })
                            }
                        })
                        analytics.logEvent(AnalyticsEvent.DOWNLOADED_OFFER_PDF, {
                            //TODO: Add this stat when the ids are available
                            //offerId,
                            userId: orgUser?.userId,
                            orgId: orgUser?.org.id,
                            orgUserId: orgUser?.id,
                        })
                    }}>
                        <CloudDownload />
                    </IconButton>
                ) : null}
                {!drafts && grants[Grant.SEND_OFFER] ? (
                    <IconButton aria-label="send-selected" onClick={() => {
                        if (appUser && org && orgUser) {
                            selected.forEach(offerId => {
                                sendOfferEmail(offerId, org.data.id, appUser, orgUser, enqueueSnackbar, 'Anbud sendt')
                            })
                        }
                    }}>
                        <Send />
                    </IconButton>
                ) : null}
                <IconButton aria-label="uncheck-all" onClick={() => setSelected([])} edge="end">
                    <Close />
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem >
    ) : null;

    const content = loading ? <FullscreenLoading screenId="Offers" /> : (
        <List>
            {multiOptions}
            {filtered?.map(offer => OfferItem({
                offer: offer,
                drafts,
                selected: selected.includes(offer.data.id),
                setSelected(isSelected: boolean) {
                    setSelected(isSelected ? Array.from(new Set([
                        ...selected,
                        offer.data.id,
                    ])) : selected.filter(offerId => offerId !== offer.data.id))
                }
            }))}
            {filtered?.length ? null : (
                orgUser ? (
                    <ListItem component={Link} to="/opprett-anbud">
                        <ListItemText primary={noOffersString} secondary="Trykk her for å komme i gang" />
                    </ListItem>
                ) : (
                    <ListItem>
                        <ListItemText primary={noOffersString} />
                    </ListItem>
                )
            )}
        </List>
    )

    return (
        <PageNavigation title={drafts ? "Utkast" : "Mine Anbud"} setSearch={setSearch} search={search}>
            <Snacbars error={error?.message} errorEvent={drafts ? AnalyticsEvent.DISMISSED_OFFER_DRAFTS_ERROR : AnalyticsEvent.DISMISSED_OFFERS_ERROR} />
            {orgUser ? (
                <Fab
                    color="secondary"
                    aria-label="add-offer"
                    style={{ position: "fixed", bottom: 20, right: 20, zIndex: 1050 }}
                    component={Link}
                    to="/opprett-anbud"
                >
                    <Add />
                </Fab>
            ) : null}
            {content}
        </PageNavigation>
    )
}
